import {
  ARENA_REPLAY_MS,
  type ArenaEvent,
  type ChallengeEvent,
  type CpuRecordEvent,
  type QuestDoneEvent,
} from '@xox/shared'
import { create } from 'zustand'
import { feedback } from '../lib/feedback'
import { readJson, writeJson } from '../lib/storage'

const REMEMBER = 40
/** Seen ids live here too, not just in memory, so a reload doesn't replay one this device already saw. */
const SEEN_KEY = 'xox-arena-seen'
/** Pending challenge cards on screen at once; older ones make room. */
const MAX_CHALLENGES = 3
const MAX_TOASTS = 2

interface ArenaState {
  /** The record notice on screen, if any. */
  notice: CpuRecordEvent | null
  /** Challenges waiting for an answer, oldest first. */
  challenges: ChallengeEvent[]
  /** Quest completions not yet dismissed, oldest first. */
  toasts: QuestDoneEvent[]
  /** How many players have the arena open; null until the feed says. */
  online: number | null
  /** Recent event ids, so a replayed frame never shows twice — persisted across reloads. */
  seen: string[]
  receive: (ev: ArenaEvent, now?: number) => void
  dismiss: () => void
  dismissChallenge: (id: string) => void
  dismissToast: (id: string) => void
}

export const useArena = create<ArenaState>((set, get) => {
  /** Marks `ev` seen; false when it was already, or is too old to matter. */
  const fresh = (ev: ArenaEvent, expiresAt: number, now: number): boolean => {
    const { seen } = get()
    if (seen.includes(ev.id) || now >= expiresAt) return false
    const nextSeen = [...seen.slice(-(REMEMBER - 1)), ev.id]
    writeJson(SEEN_KEY, nextSeen)
    set({ seen: nextSeen })
    return true
  }
  return {
    notice: null,
    challenges: [],
    toasts: [],
    online: null,
    seen: readJson<string[]>(SEEN_KEY, []),
    receive(ev, now = Date.now()) {
      switch (ev.type) {
        case 'presence':
          set({ online: ev.online })
          return
        case 'cpuRecord':
          if (!fresh(ev, ev.at + ARENA_REPLAY_MS, now)) return
          feedback(ev.kind === 'win' ? 'win' : 'react')
          set({ notice: ev })
          return
        case 'challenge':
          if (!fresh(ev, ev.expiresAt, now)) return
          feedback('seat')
          set({ challenges: [...get().challenges, ev].slice(-MAX_CHALLENGES) })
          return
        case 'questDone':
          if (!fresh(ev, ev.at + ARENA_REPLAY_MS, now)) return
          feedback('cheer')
          set({ toasts: [...get().toasts, ev].slice(-MAX_TOASTS) })
          return
      }
    },
    dismiss() {
      set({ notice: null })
    },
    dismissChallenge(id) {
      set({ challenges: get().challenges.filter((c) => c.id !== id) })
    },
    dismissToast(id) {
      set({ toasts: get().toasts.filter((t) => t.id !== id) })
    },
  }
})
